import {
  AssetSchema,
  ContactInfoSchema,
  PriceMechanism,
  EnergyEstimationMode,
  EventLosses,
  EventSchema,
  EventCause,
  EventSource,
  EventType,
  GSMetricName,
  GaugeModel,
  HorizonLabel,
  InvoiceCancelActions,
  InvoiceExternalStatus,
  InvoiceLineSchema,
  InvoiceSchema,
  InvoiceSchemaShort,
  InvoiceStatus,
  MachineTechno,
  MachineType,
  MetricHorizon,
  NoteQueryModelName,
  NoteSchema,
  NoteTagLevel,
  SiteSchema,
  Unit,
  CommissioningSite,
  InvoiceSubmitChannel,
  InvoiceSource,
} from "#imports"
import { CogeType } from "~/composables/codegen/api-client"
import type { EventSchemaWithSilence, InvoiceComputeTarget } from "~/utils/schemas"
// Type-only: filters.ts imports this module at runtime.
import type { IsValidatedChoices } from "~/utils/filters"
import { z } from "zod"

export function getEffectiveInvoiceSource(invoice: {
  source?: InvoiceSource | null
  contract: { invoice_source: InvoiceSource }
}): InvoiceSource {
  return invoice.source ?? invoice.contract.invoice_source
}

export function matchesEffectiveInvoiceSource(
  invoice: { source?: InvoiceSource | null; contract: { invoice_source: InvoiceSource } },
  source: InvoiceSource,
): boolean {
  return getEffectiveInvoiceSource(invoice) === source
}

// ______ Metric Related ______

export const mapGSMetricNameUnit: Record<GSMetricName | "availability", Unit> = {
  [GSMetricName.enum.revenue]: Unit.enum.eur,
  [GSMetricName.enum.production]: Unit.enum.kWh,
  [GSMetricName.enum.availability_injection]: Unit.enum.dimensionless,
  [GSMetricName.enum.availability_detailed]: Unit.enum.dimensionless,
  [GSMetricName.enum.availability_energy_prorated]: Unit.enum.dimensionless,
  [GSMetricName.Enum.availability_technical]: Unit.enum.dimensionless,
  availability: Unit.enum.dimensionless,
  [GSMetricName.enum.trs]: Unit.enum.dimensionless,
  [GSMetricName.enum.trs_business_plan_estimated]: Unit.enum.dimensionless,
  [GSMetricName.enum.trs_operational_estimated]: Unit.enum.dimensionless,
  [GSMetricName.enum.trs_operational_attained]: Unit.enum.dimensionless,
  [GSMetricName.enum.pr]: Unit.enum.dimensionless,
  [GSMetricName.enum.pr_business_plan_estimated]: Unit.enum.dimensionless,
  [GSMetricName.enum.pr_operational_estimated]: Unit.enum.dimensionless,
  [GSMetricName.enum.pr_operational_attained]: Unit.enum.dimensionless,
  [GSMetricName.enum.self_consumption_rate]: Unit.enum.dimensionless,
  [GSMetricName.enum.self_production_rate]: Unit.enum.dimensionless,
  [GSMetricName.enum.coverage_rate]: Unit.enum.dimensionless,
}

export const gaugeMetricLabels: HorizonLabel[] = [
  HorizonLabel.enum.current_year,
  HorizonLabel.enum.ytd,
  HorizonLabel.enum.yt_last_month,
  HorizonLabel.enum.yt_quarter,
  HorizonLabel.enum.last_year,
  HorizonLabel.enum.yesterday,
]

export const metricHorizonCommonPool: MetricHorizon[] = [
  MetricHorizon.enum.yesterday,
  MetricHorizon.enum.last_month,
  MetricHorizon.enum.yt_last_month,
  MetricHorizon.enum.ytd,
]

export const reportMetricsHorizons: MetricHorizon[] = [
  MetricHorizon.enum.yt_last_month,
  MetricHorizon.enum.ytd,
  MetricHorizon.enum.last_year,
  MetricHorizon.enum.yt_quarter,
]

export const mapMetricNameAvailableHorizons: Record<string, MetricHorizon[]> = {
  [MetricName.enum.data_health]: [MetricHorizon.enum.yt_last_month, MetricHorizon.enum.last_month],
  //
  [MetricName.enum.financial_report]: reportMetricsHorizons,
  [MetricName.enum.production_report]: reportMetricsHorizons,
  //
  [MetricName.enum.power_production]: metricHorizonCommonPool,
  [MetricName.enum.revenue]: metricHorizonCommonPool,
  [MetricName.enum.incident_energy_when_energy]: metricHorizonCommonPool,
  [MetricName.enum.availability_rate]: metricHorizonCommonPool,
  [MetricName.enum.trs]: metricHorizonCommonPool,
  [MetricName.enum.pr]: metricHorizonCommonPool,
  assetowner: [
    MetricHorizon.enum.ytd,
    MetricHorizon.enum.yt_last_month,
    MetricHorizon.enum.yesterday,
  ],
}

// __________ Techno utils __________

export function getMachineTechno(obj: SiteSchema | AssetSchema): MachineTechno {
  const mapMachineTypeTechno: { [key in MachineTechno]: MachineType[] } = {
    [MachineTechno.enum.wind_turbine]: [
      MachineType.enum.wind_turbine_onshore,
      MachineType.enum.wind_turbine_offshore,
    ],
    [MachineTechno.enum.solar_field]: [
      MachineType.enum.solar_field_canopy,
      MachineType.enum.solar_field_rooftop,
      MachineType.enum.solar_field_ground_mounted,
    ],
    [MachineTechno.enum.hydro_turbine]: [
      MachineType.enum.hydro_turbine_reservoir,
      MachineType.enum.hydro_turbine_run_of_river,
      MachineType.enum.hydro_turbine_pumped_storage,
    ],
    [MachineTechno.enum.cogeneration]: [
      MachineType.enum.cogeneration_biomass,
      MachineType.enum.cogeneration_other,
      MachineType.enum.cogeneration_waste,
    ],
    [MachineTechno.enum.consumption_site]: [MachineType.enum.consumption_site_industry_retail],
    [MachineTechno.enum.battery]: [MachineType.enum.battery_default],
  }

  if ("machine_type" in obj) {
    // Case of Site
    const entry = R.pipe(
      R.entries(mapMachineTypeTechno),
      R.find(([_, types]) => types.includes((obj as SiteSchema).machine_type as MachineType)),
    )
    return entry![0] as MachineTechno
  } else {
    // Case of Asset
    if ("manufacturer" in obj) {
      return MachineTechno.enum.wind_turbine
    } else if ("orientation" in obj) {
      return MachineTechno.enum.solar_field
    } else if ("energy_capacity" in obj) {
      return MachineTechno.enum.battery
    } else if (obj.type === "industry_retail") {
      return MachineTechno.enum.consumption_site
    } else if (R.isIncludedIn(obj.type, CogeType.options)) {
      return MachineTechno.enum.cogeneration
    } else {
      return MachineTechno.enum.hydro_turbine
    }
  }
}

export function isMachineTechno(
  obj: SiteSchema | AssetSchema,
  machineTechno: MachineTechno,
): boolean {
  return getMachineTechno(obj) === machineTechno
}

export const mapTechnoMachineTypes: Record<MachineTechno, MachineType[]> = {
  [MachineTechno.enum.solar_field]: [
    MachineType.enum.solar_field_canopy,
    MachineType.enum.solar_field_rooftop,
    MachineType.enum.solar_field_ground_mounted,
  ],
  [MachineTechno.enum.wind_turbine]: [
    MachineType.enum.wind_turbine_onshore,
    MachineType.enum.wind_turbine_offshore,
  ],
  [MachineTechno.enum.hydro_turbine]: [
    MachineType.enum.hydro_turbine_run_of_river,
    MachineType.enum.hydro_turbine_pumped_storage,
    MachineType.enum.hydro_turbine_reservoir,
  ],
  [MachineTechno.enum.cogeneration]: [
    MachineType.enum.cogeneration_biomass,
    MachineType.enum.cogeneration_waste,
    MachineType.enum.cogeneration_other,
  ],
  [MachineTechno.enum.consumption_site]: [MachineType.enum.consumption_site_industry_retail],
  [MachineTechno.enum.battery]: [MachineType.enum.battery_default],
}

export function getM0TechnoTag(site: { machine_type: string }): string | undefined {
  const techno = getMachineTechno(site as SiteSchema)
  const map: Partial<Record<MachineTechno, string>> = {
    [MachineTechno.enum.wind_turbine]: "WON",
    [MachineTechno.enum.solar_field]: "SR",
    [MachineTechno.enum.hydro_turbine]: "HY",
  }
  return map[techno]
}

// __________ Site utils __________

export function sortSitesByName<T extends { name: string }>(sites: readonly T[]): T[] {
  return [...sites].sort((a, b) => a.name.localeCompare(b.name))
}

export function formatSiteNames(
  sites: readonly { name: string }[],
  { truncate = true }: { truncate?: boolean } = {},
): string {
  const sortedSites = sortSitesByName(sites)

  if (truncate && sortedSites.length > 1) {
    return `${sortedSites[0]!.name} (+${sortedSites.length - 1})`
  }

  return sortedSites.map((site) => site.name).join(", ")
}

// __________ Contract utils __________

export function deslugContractType(str: string): string {
  const renames: { [key: string]: string } = {
    da: "PPA DA",
    fix: "PPA Fix",
    energy_efficiency: "EE",
  }
  str = renames[str] || str
  return str.toUpperCase().replaceAll("_", " ")
}

export function toContractRepr(
  contract: {
    price_mechanism?: string | PriceMechanism | undefined
    type: { name: string }
    sites: { name: string }[]
  },
  t: (value: string) => string,
): string {
  let parts: string[]
  if (R.isNonNullish(contract.price_mechanism)) {
    parts = [
      t(`site.contract_status.${contract.price_mechanism}`),
      deslugContractType(contract.type.name),
      formatSiteNames(contract.sites),
    ]
  } else {
    parts = [deslugContractType(contract.type.name), formatSiteNames(contract.sites)]
  }
  return R.join(parts, " - ")
}

const contractYearPriceMechanisms: PriceMechanism[] = [
  PriceMechanism.enum.feed_in_tarif,
  PriceMechanism.enum.contract_for_difference,
]

/**
 * Current contract year `[last anniversary, next anniversary[`, anchored on the start of the
 * oldest feed-in tariff / contract for difference. The last anniversary is strictly before today,
 * so on the anniversary day the just completed year is returned.
 * Undefined when no such contract has started yet.
 */
export function getContractYearRange(
  contracts: { start: Date; price_mechanism: PriceMechanism }[],
  now: Date = new Date(),
): [Date, Date] | undefined {
  const starts = contracts
    .filter((c) => R.isIncludedIn(c.price_mechanism, contractYearPriceMechanisms))
    .map((c) => dt.startOfDay(c.start))
  const today = dt.startOfDay(now)
  if (!R.hasAtLeast(starts, 1)) return undefined

  const reference = dt.min(starts)
  if (!dt.isBefore(reference, today)) return undefined

  // Date overflow turns a Feb 29 anniversary into Mar 1 on non-leap years
  const anniversaryIn = (year: number) => new Date(year, reference.getMonth(), reference.getDate())

  let start = anniversaryIn(today.getFullYear())
  if (!dt.isBefore(start, today)) start = anniversaryIn(today.getFullYear() - 1)
  return [start, anniversaryIn(start.getFullYear() + 1)]
}

export function toHorizonProgress(start: Date | string, end: Date | string): number {
  start = z.coerce.date().parse(start)
  end = z.coerce.date().parse(end)
  const now = new Date()
  const progress = Math.round(((Number(now) - Number(start)) / (Number(end) - Number(start))) * 100)
  if (progress < 0) {
    return 0
  }
  if (progress > 100) {
    return 100
  }
  return progress
}

// __________ Invoices utils __________

export function toInvoiceRepr(invoice: {
  sites: { name: string }[]
  start: Date | string
  end: Date | string
}): string {
  return R.join(
    [
      formatSiteNames(invoice.sites),
      toISODate(invoice.start, false),
      toISODate(invoice.end, false),
    ],
    " - ",
  )
}

export const mapInvoiceStatusColor: Record<InvoiceStatus, string> = {
  [InvoiceStatus.enum.error]: "red",
  [InvoiceStatus.enum.incomplete]: "yellow",
  [InvoiceStatus.enum.computed]: "rnxgreen",
  //
  [InvoiceStatus.enum.capped]: "rnxorange",
  [InvoiceStatus.enum.informative]: "rnxblue",
  //
  [InvoiceStatus.enum.draft]: "rnxorange",
  [InvoiceStatus.enum.waiting_for_publication]: "rnxorange",
  [InvoiceStatus.enum.published]: "cyan",
  [InvoiceStatus.enum.validated]: "rnxgreen",
  //
  [InvoiceStatus.enum.waiting]: "rnxorange",
  [InvoiceStatus.enum.submitting]: "blue",
  [InvoiceStatus.enum.submitted]: "sky",
  [InvoiceStatus.enum.payment]: "green",
  [InvoiceStatus.enum.rejected]: "red",
  [InvoiceStatus.enum.paid]: "green",
  [InvoiceStatus.enum.production_null]: "yellow",
  [InvoiceStatus.enum.canceled_by_credit_note]: "red",
  [InvoiceStatus.enum.to_validate]: "rnxorange",
  [InvoiceStatus.enum.to_redo]: "rnxorange",
  //
  [InvoiceStatus.enum.deleted]: "slate",
  [InvoiceStatus.enum.e_invoice_transmitted]: "sky",
  [InvoiceStatus.enum.pdf_generation_failed]: "red",
  [InvoiceStatus.enum.e_invoice_generation_failed]: "red",
}

export function isStatusMismatch(external_status: InvoiceExternalStatus | null | undefined) {
  const mismatch_statuses = ["energy_mismatch", "price_mismatch", "energy_and_price_mismatch"]
  return !R.isNullish(external_status) ? mismatch_statuses.includes(external_status) : false
}

export interface CheckWithReason {
  valid: boolean
  reason?: string
}

function isComputableStatus(status: InvoiceStatus): boolean {
  const allowedStatus: string[] = [
    InvoiceStatus.enum.error,
    InvoiceStatus.enum.waiting,
    InvoiceStatus.enum.incomplete,
    InvoiceStatus.enum.computed,
    InvoiceStatus.enum.to_redo,
    InvoiceStatus.enum.draft,
    InvoiceStatus.enum.informative,
  ]
  return allowedStatus.includes(status)
}

function isInvoiceRecomputable(invoice: InvoiceSchemaShort): CheckWithReason {
  const canCompute =
    (R.isIncludedIn(invoice.contract.invoice_source, [
      InvoiceSource.enum.renewex,
      InvoiceSource.enum.assetmanager,
    ]) &&
      isComputableStatus(invoice.status)) ||
    R.isIncludedIn(invoice.contract.invoice_source, [
      // can recompute no matter the status
      InvoiceSource.enum.buyer_with_rnx_input,
      InvoiceSource.enum.buyer,
      InvoiceSource.enum.renewex_with_buyer_input,
    ])

  let reason: undefined | string
  if (!canCompute) {
    reason = `${formatSiteNames(invoice.sites)}: Invoice with ${invoice.status} status cannot be computed.`
  }
  return { valid: canCompute, reason }
}

// A credit-note line is the counterpart of an already invoiced line: the back end never
// recomputes nor unpublishes an invoice holding internal ones, a credit note as a whole or a
// partial one.
function holdsCreditNoteLines(invoice: InvoiceSchemaShort): boolean {
  return invoice.is_only_credit_note || invoice.lines.some((line) => line.is_credit_note)
}

export function isInvoiceComputable(invoice: InvoiceSchemaShort): CheckWithReason {
  if (holdsCreditNoteLines(invoice)) {
    return {
      valid: false,
      reason: `${formatSiteNames(invoice.sites)}: a credit note can't be computed.`,
    }
  }
  return isInvoiceRecomputable(invoice)
}

// Mirrors InvoiceStatus.base_refreshable (zefire/logic/clause/invoicing_logic.py)
const BASE_REFRESHABLE_STATUSES: InvoiceStatus[] = [
  InvoiceStatus.enum.waiting,
  InvoiceStatus.enum.error,
  InvoiceStatus.enum.incomplete,
  InvoiceStatus.enum.draft,
  InvoiceStatus.enum.computed,
  InvoiceStatus.enum.waiting_for_publication,
  InvoiceStatus.enum.to_redo,
  InvoiceStatus.enum.informative,
]

// Mirrors the `is_refreshable` column property of zefire's Invoice model
// (models/invoice.py): whether the invoice can still be refreshed.
export function isInvoiceRefresheable(invoice: InvoiceSchemaShort): boolean {
  const source = invoice.contract.invoice_source
  // Invoices issued by counterparts can always be refreshed
  if (R.isIncludedIn(source, [InvoiceSource.enum.buyer, InvoiceSource.enum.buyer_with_rnx_input])) {
    return true
  }
  if (source == InvoiceSource.enum.renewex_with_buyer_input) {
    return R.isIncludedIn(invoice.status, [
      ...BASE_REFRESHABLE_STATUSES,
      InvoiceStatus.enum.to_validate,
      InvoiceStatus.enum.published,
    ])
  }
  return R.isIncludedIn(invoice.status, BASE_REFRESHABLE_STATUSES)
}

// Statuses where the invoice is not final yet: its content can still change,
// so its indexes/energies remain editable. Final ones (published, validated,
// submitted, paid, deleted...) freeze the invoice.
const NON_FINAL_STATUSES: InvoiceStatus[] = [
  InvoiceStatus.enum.error,
  InvoiceStatus.enum.incomplete,
  InvoiceStatus.enum.waiting,
  InvoiceStatus.enum.draft,
  InvoiceStatus.enum.computed,
  InvoiceStatus.enum.waiting_for_publication,
  InvoiceStatus.enum.to_validate,
  InvoiceStatus.enum.to_redo,
  InvoiceStatus.enum.informative,
]

// The invoice extra (indexes in index mode, energies in load profile mode) can
// only be edited while the invoice is not final.
export function isInvoiceExtraEditable(invoice: InvoiceSchemaShort): boolean {
  return R.isIncludedIn(invoice.status, NON_FINAL_STATUSES)
}

// invoice lines can be edited when the invoice is computable
export function isInternalInvoiceLineEditable(invoice: InvoiceSchemaShort): boolean {
  return (
    isComputableStatus(invoice.status) &&
    invoice.contract.invoice_source == InvoiceSource.enum.renewex
  )
  // in case of renewex_with_buyer_input, there is no need to update the internal lines,
  // the user can validate to accept the external lines
}

export function canSetInvoiceAsComputed(invoice: InvoiceSchemaShort): boolean {
  return (
    invoice.status == InvoiceStatus.enum.incomplete &&
    (R.isIncludedIn(invoice.contract.invoice_source, [
      InvoiceSource.enum.renewex,
      InvoiceSource.enum.renewex_with_buyer_input,
      InvoiceSource.enum.buyer_with_rnx_input,
    ]) ||
      invoice.contract.invoice_submit_channel == InvoiceSubmitChannel.enum.platform)
  )
}

export function canSetInvoiceAsValidated(invoice: InvoiceSchemaShort): boolean {
  return (
    invoice.external_lines.length > 0 &&
    R.isIncludedIn(invoice.status, [
      InvoiceStatus.enum.to_validate,
      InvoiceStatus.enum.incomplete,
    ]) &&
    invoice.contract.invoice_source == InvoiceSource.enum.renewex_with_buyer_input
    // TODO: once edfoa "accept" / "refuse" actions are implemented:
    // && invoice.contract.invoice_source == InvoiceSource.enum.buyer_with_rnx_input
  )
}

export function canSetToWaitingForPublication(invoice: InvoiceSchemaShort): boolean {
  return (
    invoice.lines.length > 0 &&
    invoice.contract.invoice_source === InvoiceSource.enum.assetmanager &&
    R.isIncludedIn(invoice.status, [InvoiceStatus.enum.computed, InvoiceStatus.enum.draft]) &&
    !isInvoicingOnHold(invoice)
  )
}

export function canUnpublishWaitingPublicationInvoice(invoice: InvoiceSchemaShort): boolean {
  return invoice.status === InvoiceStatus.enum.waiting_for_publication
}

// A contract whose invoicing is on hold must not have its invoices sent out to
// the buyer (publish / submit / next excel / zero-submission). Internal work
// (compute, draft publication, downloads, attachments…) stays available.
export function isInvoicingOnHold(invoice: InvoiceSchemaShort): boolean {
  return R.isTruthy(invoice.contract.invoicing_on_hold_reasons?.length)
}

function onHoldReason(invoice: InvoiceSchemaShort): string {
  const reasons = invoice.contract.invoicing_on_hold_reasons ?? []
  return `${formatSiteNames(invoice.sites)}: invoicing is on hold (${reasons.join(", ")}).`
}

export function canSetInvoiceAsPaid(invoice: InvoiceSchemaShort): boolean {
  const isPayableStatus = R.isIncludedIn(invoice.status, [
    InvoiceStatus.enum.published,
    InvoiceStatus.enum.submitted,
    InvoiceStatus.enum.payment,
  ])

  // autoinvoicing (self-invoicing) invoices stay in "validated" status and never get published,
  // so they must be markable as paid from there.
  const isValidatedAutoinvoicing =
    invoice.status == InvoiceStatus.enum.validated &&
    invoice.contract.invoice_source == InvoiceSource.enum.buyer

  return isPayableStatus || isValidatedAutoinvoicing
}

export function canSetInvoiceAsAccounted(invoice: InvoiceSchemaShort): boolean {
  // Keep aligned with the backend's InvoiceStatus.accountable; do not restamp accounted invoices.
  return (
    !invoice.is_accounted &&
    R.isIncludedIn(invoice.status, [
      InvoiceStatus.enum.submitted,
      InvoiceStatus.enum.payment,
      InvoiceStatus.enum.paid,
      InvoiceStatus.enum.canceled_by_credit_note,
      InvoiceStatus.enum.rejected,
    ])
  )
}

// Status / source / channel part of the submission check, without the on-hold gate:
// attachments can still be uploaded while the invoicing is on hold.
function isInvoiceSubmittableStatus(invoice: InvoiceSchemaShort): CheckWithReason {
  const isFromRenewex =
    invoice.contract.invoice_source == InvoiceSource.enum.renewex &&
    R.isIncludedIn(invoice.contract.invoice_submit_channel, [
      InvoiceSubmitChannel.enum.email,
      InvoiceSubmitChannel.enum.generix,
      InvoiceSubmitChannel.enum.cegedim,
    ]) &&
    R.isIncludedIn(invoice.status, [
      InvoiceStatus.enum.draft,
      InvoiceStatus.enum.computed,
      InvoiceStatus.enum.published,
    ])

  const isFromRenewexWithBuyerInput =
    invoice.contract.invoice_source == InvoiceSource.enum.renewex_with_buyer_input &&
    R.isIncludedIn(invoice.contract.invoice_submit_channel, [
      InvoiceSubmitChannel.enum.email,
      InvoiceSubmitChannel.enum.generix,
      InvoiceSubmitChannel.enum.cegedim,
    ]) &&
    R.isIncludedIn(invoice.status, [
      InvoiceStatus.enum.validated, // computed invoice aren't publishable / submittable
      InvoiceStatus.enum.draft,
      InvoiceStatus.enum.published,
    ])

  const isOnEDFOAPlatform =
    invoice.contract.invoice_source == InvoiceSource.enum.buyer_with_rnx_input &&
    invoice.contract.invoice_submit_channel == InvoiceSubmitChannel.enum.platform &&
    R.isIncludedIn(invoice.status, [InvoiceStatus.enum.computed])

  const isFromAssetmanager =
    invoice.contract.invoice_source == InvoiceSource.enum.assetmanager &&
    R.isIncludedIn(invoice.contract.invoice_submit_channel, [
      InvoiceSubmitChannel.enum.email,
      InvoiceSubmitChannel.enum.generix,
      InvoiceSubmitChannel.enum.cegedim,
    ]) &&
    invoice.has_pdf &&
    // pdf are generated async, after the publication_id is attached,
    // so it might not be always avaialable and it is required to submit
    // NOTE: the backend logic checks that the pdf to be submitted isn't the draft (using the gen_input)
    R.isIncludedIn(invoice.status, [InvoiceStatus.enum.published]) // need to be published first

  const canSubmit =
    isFromRenewex || isOnEDFOAPlatform || isFromRenewexWithBuyerInput || isFromAssetmanager
  let reason: undefined | string
  if (!canSubmit) {
    reason = `${formatSiteNames(invoice.sites)}: Invoice with ${invoice.status} comming from ${invoice.contract.invoice_source} status can be submitted.`
  }

  return { valid: canSubmit, reason }
}

export function isInvoiceSubmittable(invoice: InvoiceSchemaShort): CheckWithReason {
  // an internal credit note belongs to the seller's accounting only: never submitted
  if (invoice.is_internal_credit_note) {
    return {
      valid: false,
      reason: `${formatSiteNames(invoice.sites)}: an internal credit note is never submitted.`,
    }
  }
  if (isInvoicingOnHold(invoice)) {
    return { valid: false, reason: onHoldReason(invoice) }
  }
  return isInvoiceSubmittableStatus(invoice)
}

export function isInvoiceAttachmentUploadable(invoice: InvoiceSchemaShort): CheckWithReason {
  const isPPAFix =
    invoice.contract.type.price_mechanism == PriceMechanism.enum.ppa &&
    invoice.contract.type.name === "fix"
  const isAutoconso = invoice.contract.type.name == "autoconso"

  const canUpload = (isPPAFix || isAutoconso) && isInvoiceSubmittableStatus(invoice).valid

  let reason: undefined | string
  if (!canUpload) {
    reason = `${formatSiteNames(invoice.sites)}: Invoice of contract type ${invoice.contract.type.name} with ${invoice.status} status can't have an attachment uploaded .`
  }
  return { valid: canUpload, reason }
}

export function isInvoicePublishable(
  invoice: InvoiceSchemaShort,
  options: { asDraft?: boolean } = {},
): CheckWithReason {
  if (invoice.contract.invoice_submit_channel == InvoiceSubmitChannel.enum.platform) {
    return {
      valid: false,
      reason: `${formatSiteNames(invoice.sites)}: Platform invoice can't be published`,
    }
  }

  // a draft publication stays internal, so it remains possible while on hold
  if (!options.asDraft && isInvoicingOnHold(invoice)) {
    return { valid: false, reason: onHoldReason(invoice) }
  }

  const canPublish =
    (invoice.contract.invoice_source == InvoiceSource.enum.renewex &&
      R.isIncludedIn(invoice.status, [InvoiceStatus.enum.draft, InvoiceStatus.enum.computed])) ||
    //
    (invoice.contract.invoice_source == InvoiceSource.enum.assetmanager &&
      R.isIncludedIn(invoice.status, [InvoiceStatus.enum.draft, InvoiceStatus.enum.computed]) &&
      !!options.asDraft) ||
    //
    (invoice.contract.invoice_source == InvoiceSource.enum.renewex_with_buyer_input &&
      R.isIncludedIn(invoice.status, [InvoiceStatus.enum.draft, InvoiceStatus.enum.validated]))

  const channelRequiresPublication = R.isIncludedIn(invoice.contract.invoice_submit_channel, [
    InvoiceSubmitChannel.enum.pdf,
    InvoiceSubmitChannel.enum.email,
    InvoiceSubmitChannel.enum.generix,
    InvoiceSubmitChannel.enum.cegedim,
  ])
  let reason: undefined | string
  if (!canPublish) {
    reason = `${formatSiteNames(invoice.sites)}: Invoice with ${invoice.status} status can be submitted.`
  }
  if (!channelRequiresPublication) {
    reason = `${formatSiteNames(invoice.sites)}: Channel ${invoice.contract.invoice_submit_channel} doesn't require to publish an invoice`
  }

  return { valid: canPublish && channelRequiresPublication, reason }
}

export function isInvoiceCancelable(
  invoice: InvoiceSchemaShort,
  cancel_action: InvoiceCancelActions,
): CheckWithReason {
  if (invoice.contract.invoice_submit_channel === InvoiceSubmitChannel.enum.platform) {
    return {
      valid: false,
      reason: `${formatSiteNames(invoice.sites)}: Invoice with submit chanel ${invoice.contract.invoice_submit_channel} can't be canceled`,
    }
  }

  if (
    invoice.contract.invoice_source === InvoiceSource.enum.assetmanager &&
    cancel_action != InvoiceCancelActions.enum.credit_note
  ) {
    // only credit note is authorized
    return {
      valid: false,
      reason: `${formatSiteNames(invoice.sites)}: Invoice with publication id from ${invoice.contract.invoice_submit_channel} can't be canceled`,
    }
  }

  // cancellation is forbidden while the invoicing is on hold
  if (isInvoicingOnHold(invoice)) {
    return { valid: false, reason: onHoldReason(invoice) }
  }

  let canCancel: boolean = false

  if (cancel_action === InvoiceCancelActions.enum.unpublish) {
    if (holdsCreditNoteLines(invoice)) {
      return {
        valid: false,
        reason: `${formatSiteNames(invoice.sites)}: a credit note can't be unpublished.`,
      }
    }
    // an accounted invoice has already been communicated to the external
    // accounting system and can no longer be unpublished
    if (invoice.is_accounted) {
      return {
        valid: false,
        reason: `${formatSiteNames(invoice.sites)}: an accounted invoice can't be unpublished.`,
      }
    }
    canCancel = invoice.status === InvoiceStatus.enum.published
  }
  if (cancel_action === InvoiceCancelActions.enum.credit_note) {
    canCancel =
      invoice.status === InvoiceStatus.enum.published ||
      invoice.status === InvoiceStatus.enum.paid ||
      invoice.status === InvoiceStatus.enum.submitted
  }
  if (cancel_action === InvoiceCancelActions.enum.delete) {
    canCancel = isInvoiceRecomputable(invoice).valid
  }

  let reason: undefined | string
  if (!canCancel) {
    reason = `${formatSiteNames(invoice.sites)}: Invoice with ${invoice.status} status can't be canceled.`
  }

  return { valid: canCancel, reason }
}

export function hasInvoiceResults(invoice: InvoiceSchemaShort): boolean {
  return Boolean(invoice.lines.length + invoice.external_lines.length)
}

export function invoiceHasExcel(invoice: InvoiceSchemaShort): boolean {
  return Boolean(invoice.lines.length)
}

export function isRegulationInvoice(invoice: InvoiceSchemaShort): boolean {
  // An invoice is a regulation one when all lines from either source are regulation lines.
  // This is different from "containing" regulation, which only requires one regulation line.
  return [invoice.lines, invoice.external_lines].some(
    (lines) => lines.length > 0 && lines.every((line) => line.is_regulation),
  )
}

/**
 * Period sort key of an invoice, to be ordered descending (newest first): by end, then by horizon
 * length so that a regularisation spanning several months comes before the last month it covers,
 * then a regularisation before the regular invoice of the same horizon.
 */
export function invoicePeriodSortKey(invoice: InvoiceSchemaShort): string {
  const duration = invoice.end.getTime() - invoice.start.getTime()

  return [
    invoice.end.toISOString(),
    String(duration).padStart(15, "0"),
    Number(isRegulationInvoice(invoice)),
  ].join(" ")
}

export function getInvoiceTotalAmount(invoice: InvoiceSchemaShort) {
  const total = invoice.lines.find((line) => line.product === "total")?.amount
  const externalTotal = invoice.external_lines.find((line) => line.product === "total")?.amount
  return externalTotal || total
}

interface TotalLine {
  quantity_unit: string
  quantity: number
  amount: number
  price: number
}

function _aggregateInvoiceLines(lines: InvoiceLineSchema[]): TotalLine {
  if (lines.length === 0) {
    throw new Error("empty lines")
  }

  let summableLines = lines
  if (lines.length > 1) {
    summableLines = lines.filter((l) => l.is_summable)
  }

  const quantity_unit = R.first(summableLines.map((line) => line.quantity_unit))

  let quantity = 1
  if (quantity_unit !== Unit.enum.dimensionless) {
    quantity = R.sumBy(summableLines, (l) => l.quantity)
  }
  const price = R.pipe(
    summableLines,
    R.filter((e) => e.quantity !== 0),
    R.meanBy((e) => e.price),
  )
  const amount = R.sumBy(lines, (l) => l.amount)

  // Careful to cleanup potential NaNs
  const aggLine: TotalLine = {
    quantity: Number.isNaN(quantity) ? 0 : quantity,
    amount: Number.isNaN(amount) ? 0 : amount,
    price: Number.isNaN(price) ? 0 : price,
    quantity_unit: quantity_unit ?? Unit.enum.dimensionless,
  }

  return aggLine
}

export interface InvoiceTotalLines {
  total_line?: TotalLine
  total_external_line?: TotalLine
  bestof_total_line?: TotalLine
}

/**
 * VAT rates are frozen per line at creation, so the lines are the source of truth.
 * Zero-rate lines and zero-amount lines carry no VAT, so they must not reveal VAT columns
 * nor qualify the invoice total as "excl. VAT".
 */
export function hasVatLines(lines: InvoiceLineSchema[]): boolean {
  return lines.some(
    (line) => R.isNonNullish(line.vat_rate) && line.vat_rate > 0 && line.amount !== 0,
  )
}

/**
 * Whether the lines carry several distinct targets, in which case the target column is worth
 * showing: a target shared by every line would repeat the same label on each of them.
 */
export function hasMultipleTargets(lines: InvoiceLineSchema[]): boolean {
  return R.unique(lines.map((line) => line.target.uuid)).length > 1
}

/**
 * Human readable label of an invoice line target, whichever kind it is
 * (site name, delivery point code, purchase order or contract name).
 */
export function invoiceLineTargetLabel(target: InvoiceLineSchema["target"]): string {
  if ("name" in target) return target.name
  if ("code" in target) return target.code ?? target.uuid
  if ("purchase_order" in target) return target.purchase_order ?? target.uuid
  return target.uuid
}

export const InvoiceKind = z.enum([
  // base
  "invoice", // a classic invoice with positive amount
  "credit", // invoice with negative amount (ex: CFD)
  // cancelation
  "canceled", // invoice or credit that has been canceled
  "reversal", // invoice used to canceled another
  // regulation
  "regulation", // a invoice used for regulation
])
export type InvoiceKind = z.infer<typeof InvoiceKind>
export const mapInvoiceKindColor: Record<InvoiceKind, string> = {
  [InvoiceKind.enum.invoice]: "sky",
  [InvoiceKind.enum.credit]: "cyan",
  [InvoiceKind.enum.canceled]: "gray",
  [InvoiceKind.enum.reversal]: "teal",
  [InvoiceKind.enum.regulation]: "lime",
}

export function computeInvoiceKind(invoice: InvoiceSchema | InvoiceSchemaShort): InvoiceKind {
  const isRegulation = isRegulationInvoice(invoice)
  if (invoice.is_only_credit_note) {
    return InvoiceKind.enum.reversal
  } else if (
    R.isIncludedIn(invoice.status, [
      InvoiceStatus.enum.canceled_by_credit_note,
      InvoiceStatus.enum.deleted,
    ])
  ) {
    return InvoiceKind.enum.canceled
  }

  // can be both regulation and canceled
  if (isRegulation) {
    return InvoiceKind.enum.regulation
  }

  const invoiceWithTotalLines = computeTotalLinesOnInvoice(invoice)
  if ((invoiceWithTotalLines.bestof_total_line?.amount ?? 0) < 0) {
    return InvoiceKind.enum.credit
  }

  return InvoiceKind.enum.invoice
}

export function computeTotalLinesOnInvoice<T extends InvoiceSchema | InvoiceSchemaShort>(
  input: T,
): T & InvoiceTotalLines {
  // clone to avoid readonly issues
  const value = R.clone(input) as T & InvoiceTotalLines

  if (value.lines.length) {
    value.total_line = _aggregateInvoiceLines(value.lines)
  }

  if (value.external_lines.length) {
    value.total_external_line = _aggregateInvoiceLines(value.external_lines)
  }

  if (!(value.total_line || value.total_external_line)) {
    value.bestof_total_line = undefined
  } else {
    value.bestof_total_line = {
      quantity_unit:
        value.total_external_line?.quantity_unit ?? value.total_line?.quantity_unit ?? "",
      quantity: value.total_external_line?.quantity ?? value.total_line?.quantity ?? 0,
      price: value.total_external_line?.price ?? value.total_line?.price ?? 0,
      amount: value.total_external_line?.amount ?? value.total_line?.amount ?? 0,
    }
  }

  return value
}

function bestofTotalAmount(invoice: InvoiceSchema | InvoiceSchemaShort): number {
  return computeTotalLinesOnInvoice(invoice).bestof_total_line?.amount ?? 0
}

// Apex's private UBL type codes of internal credit notes (kept by Apex, never sent to the
// counterpart): 3819 for an internal avoir, 3809 for the internal invoice cancelling a
// negative invoice (an avoir 262), whose total is positive.
export const InternalUBLTypeCode = z.enum(["3819", "3809"])
export type InternalUBLTypeCode = z.infer<typeof InternalUBLTypeCode>
// UBL type codes of external credit notes (sent to the counterpart): 381 for an avoir,
// 380 for the invoice cancelling a negative invoice (an avoir 262) the counterpart booked.
export const ExternalUBLTypeCode = z.enum(["381", "380"])
export type ExternalUBLTypeCode = z.infer<typeof ExternalUBLTypeCode>

/** Internal UBL type code of the credit note that would cancel the invoice. */
export function internalCancellationTypeCode(
  invoice: InvoiceSchema | InvoiceSchemaShort,
): InternalUBLTypeCode {
  return bestofTotalAmount(invoice) < 0
    ? InternalUBLTypeCode.enum["3809"]
    : InternalUBLTypeCode.enum["3819"]
}

/** External UBL type code of the credit note that would cancel the invoice. */
export function externalCancellationTypeCode(
  invoice: InvoiceSchema | InvoiceSchemaShort,
): ExternalUBLTypeCode {
  return bestofTotalAmount(invoice) < 0
    ? ExternalUBLTypeCode.enum["380"]
    : ExternalUBLTypeCode.enum["381"]
}

/** Internal UBL type code of an internal credit note. */
export function internalCreditNoteTypeCode(
  creditNote: InvoiceSchema | InvoiceSchemaShort,
): InternalUBLTypeCode {
  return bestofTotalAmount(creditNote) > 0
    ? InternalUBLTypeCode.enum["3809"]
    : InternalUBLTypeCode.enum["3819"]
}

export function toInvoiceQuantityUnitDisplay(unit: Unit | string | undefined): Unit {
  const mapInvoiceQuantityUnitDisplay: Partial<Record<Unit, Unit>> = {
    [Unit.enum.MWh]: Unit.enum.kWh,
    [Unit.enum.MW]: Unit.enum.kW,
  }
  if (!unit) return Unit.enum.dimensionless
  return mapInvoiceQuantityUnitDisplay[unit as Unit] || (unit as Unit)
}

export function formatInvoiceLine(
  invoice: (InvoiceSchemaShort | InvoiceSchema) & InvoiceTotalLines,
  key: "quantity" | "amount",
  i18n: { t: (value: string) => string; n: (value: number, cfg: any, locale?: string) => string },
): string | undefined {
  if (!isStatusMismatch(invoice.external_status)) {
    return undefined
  }

  const internal_value = invoice.total_line?.[key]
  const external_value = invoice.total_external_line?.[key]

  if (internal_value === undefined || external_value === undefined) {
    return undefined
  }

  const quantityUnitFrom = invoice.bestof_total_line?.quantity_unit || "dimensionless"
  const mapKeyUnit: { [k in typeof key]: { from: Unit; to: Unit } } = {
    quantity: {
      from: quantityUnitFrom as Unit,
      to: toInvoiceQuantityUnitDisplay(quantityUnitFrom),
    },
    amount: { from: Unit.enum.eur, to: Unit.enum.eur },
  }

  const convAndFmt = (v: number) =>
    i18n.n(
      v * getUnitConversionRate(mapKeyUnit[key].from, mapKeyUnit[key].to),
      mapKeyUnit[key].to,
      "fr-FR",
    )

  const mapKeySubject: { [k in typeof key]: string } = {
    quantity: "Prod",
    amount: "Amount",
  }

  const unit_str = i18n.t(`unit.${mapKeyUnit[key].to}`)
  let content = [
    `<td>External ${mapKeySubject[key]}</td>
     <td>${convAndFmt(external_value)} ${unit_str}</td>
    `,
    `<td>Internal ${mapKeySubject[key]}</td>
     <td>${convAndFmt(internal_value)} ${unit_str}</td>
    `,
  ]

  if (key === "quantity" && Object.keys(invoice).includes("details")) {
    const details = (invoice as InvoiceSchema).details
    if (details.length === 1) {
      const clause_details = details[0]

      const conso = (clause_details as { extra?: { conso?: number } }).extra?.conso
      if (typeof conso === "number" && conso !== 0) {
        const conso_rounded = i18n.n(conso, Unit.enum.kWh)
        content = [...content, `<td>Conso</td> <td>${conso_rounded} kWh</td>`]
      }
    }
  }

  const contentDivs = content.map((value) => `<tr>${value}</tr>`)

  return `
    <table class="table-auto text-right border-spacing-x-4 border-separate">
      ${contentDivs.join("")}
    </table>`
}

export type InvoiceWithTotalLinesT = InvoiceSchemaShort & InvoiceTotalLines & { kind: InvoiceKind }
export type GroupedInvoiceT = { children: InvoiceWithTotalLinesT[] } & InvoiceWithTotalLinesT

export function groupInvoicePerSitePeriod(invoices: InvoiceSchemaShort[]): GroupedInvoiceT[] {
  const keyedInvoices = R.pipe(
    invoices,
    R.map(computeTotalLinesOnInvoice),
    R.map((invoice) => {
      // A contract can issue several invoices on the same horizon, even for the same site
      // (delivery-point granularity). Only group versions covering the same line targets.
      const targets = R.pipe(
        [...invoice.lines, ...invoice.external_lines],
        R.map((line) => line.target.uuid),
        R.unique(),
        R.sortBy(R.identity()),
      )
      const sites = invoice.sites.map((site) => site.uuid).sort()
      const periodKey = `${isRegulationInvoice(invoice)}-${invoice.start}-${invoice.end}-${invoice.contract.uuid}-${sites.join(",")}`
      return {
        invoice: { ...invoice, kind: computeInvoiceKind(invoice) },
        periodKey,
        key: `${periodKey}-${targets.join(",")}`,
        hasTargets: targets.length > 0,
      }
    }),
  )

  // A version without lines (never computed, rejected before computation, ...) has no
  // target to match on. Attach it to the targeted group of its period when that group is
  // unambiguous; otherwise keep it apart rather than guessing the delivery point.
  const targetedKeysPerPeriod = R.pipe(
    keyedInvoices,
    R.filter((entry) => entry.hasTargets),
    R.groupBy((entry) => entry.periodKey),
    R.mapValues((entries) => R.unique(entries.map((entry) => entry.key))),
  )
  const batchInvoices = R.pipe(
    keyedInvoices,
    R.groupBy((entry) => {
      const targetedKeys = targetedKeysPerPeriod[entry.periodKey] ?? []
      return !entry.hasTargets && targetedKeys.length === 1 ? targetedKeys[0] : entry.key
    }),
    R.values(),
    R.map((entries) => entries.map((entry) => entry.invoice)),
  )

  const statusPriority: Partial<Record<InvoiceStatus, number>> = {
    [InvoiceStatus.enum.waiting]: 0,
    [InvoiceStatus.enum.canceled_by_credit_note]: 1,
    [InvoiceStatus.enum.deleted]: 2,
    [InvoiceStatus.enum.rejected]: 2,
  }
  const defaultPriority = 1
  // Rank versions by how well they stand for their group, before any date: a discarded
  // version (e.g. a duplicate rejected after the original was paid) first, then superseded
  // ones (credit notes and the invoices they cancel), the live version last. A credit note
  // is issued along with its replacement invoice, with the same dates down to sub-second
  // creation gaps, so dates alone can't elect the parent.
  const discardedStatuses: InvoiceStatus[] = [
    InvoiceStatus.enum.rejected,
    InvoiceStatus.enum.deleted,
  ]
  const standingRank = (invoice: InvoiceSchemaShort) => {
    if (discardedStatuses.includes(invoice.status)) {
      return 0
    }
    if (
      invoice.is_only_credit_note ||
      invoice.status === InvoiceStatus.enum.canceled_by_credit_note
    ) {
      return 1
    }
    return 2
  }

  const grouped: GroupedInvoiceT[] = []
  for (const batch of batchInvoices) {
    batch.sort((a, b) => {
      const rankDiff = standingRank(a) - standingRank(b)
      if (rankDiff !== 0) {
        return rankDiff
      }

      const dateFields = [
        "creation_date",
        "publication_date",
        "computation_date",
        "computable_asof",
      ] as const
      for (const field of dateFields) {
        if (R.isNonNullish(a[field]) && R.isNonNullish(b[field])) {
          const timeDiff = dt.differenceInSeconds(a[field], b[field])
          if (timeDiff !== 0) {
            return timeDiff
          }
        }
      }

      const priorityA = statusPriority[a.status] ?? defaultPriority
      const priorityB = statusPriority[b.status] ?? defaultPriority
      return priorityB - priorityA
    })
    const [children, [parent]] = R.splitAt(batch, batch.length - 1)
    grouped.push({ ...parent, children })
  }

  return grouped
}

export const emailSubmitChannels: InvoiceSubmitChannel[] = [
  InvoiceSubmitChannel.enum.email,
  InvoiceSubmitChannel.enum.generix,
  InvoiceSubmitChannel.enum.cegedim,
]

// __________ Partner utils __________

export function getImageLink(contact_info: ContactInfoSchema): string {
  if (R.isNullish(contact_info.cloud_image?.avatar)) {
    return ""
  }

  return contact_info.cloud_image.avatar
}

// __________ Gauges utils __________

export function convertGaugeUnit(
  gauge: GaugeModel,
  unit: Unit | undefined,
): GaugeModel & { unit: Unit } {
  if (unit === undefined) {
    unit = Unit.enum.dimensionless
  }

  if (typeof gauge.value !== "number") {
    return { ...gauge, unit }
  }

  let value = gauge.value!
  let toUnit: Unit = unit

  if (unit === Unit.enum.dimensionless) {
    toUnit = Unit.enum["%"]
    const ratio = getUnitConversionRate(Unit.enum.dimensionless, toUnit)
    value = ratio * value
  } else {
    const converted = autoConvertQuantity(value, unit)
    value = converted.value
    toUnit = converted.unit
  }
  return { ...gauge, value, unit: toUnit }
}

// __________ Events utils __________

export function getEventChartHorizon(event: Pick<EventSchema, "start" | "end">) {
  const start = dt.startOfDay(event.start)
  const endDay = dt.startOfDay(event.end)
  const end = dt.isEqual(event.end, endDay) ? endDay : dt.addDays(endDay, 1)

  return { start, end }
}

// Mirrors zefire's `EventSource.editable` (zefire/logic/supervision/event_schema.py): every other
// source is an external mirror (connectors) on which editing and validation are not allowed.
export const EDITABLE_EVENT_SOURCES = [
  EventSource.enum.ai,
  EventSource.enum.power_production,
  EventSource.enum.manual,
] as const

export function isEventEditable(event: EventSchema): boolean {
  return R.isIncludedIn(event.source, EDITABLE_EVENT_SOURCES)
}

// Mirrors zefire's `Event.event_type`, which is derived from the available capacity.
export function getEventTypeFromCapacity(
  availableCapacity: number,
  siteCapacity: number,
): EventType {
  if (availableCapacity === 0) return EventType.enum.stop

  if (availableCapacity === siteCapacity) return EventType.enum.informative

  return EventType.enum.downgraded
}

// Sources whose events explain a power production stop, whatever their state. `ai` is left out:
// its detections would hide too many stops. Other power production events are the same detection.
export const STOP_EXPLAINING_SOURCES = [
  EventSource.enum.manual,
  EventSource.enum.alemca,
  EventSource.enum.elmy,
  EventSource.enum.smart_oa,
] as const

// Power production stops awaiting review: neither validated nor silenced, and overlapped by no event
// of an explaining source on the same site. Validating one feeds the KPIs with a stop nothing else
// confirms. `events` may span several sites.
export function getStopsToReview<T extends EventSchemaWithSilence>(events: T[]): T[] {
  // Indexed per site: an asset manager has thousands of candidates, compared with their own site's
  // events only.
  const explainingBySite = new Map<string, T[]>()
  for (const event of events) {
    if (!R.isIncludedIn(event.source, STOP_EXPLAINING_SOURCES)) continue

    const siteEvents = explainingBySite.get(event.site.uuid)
    if (siteEvents) siteEvents.push(event)
    else explainingBySite.set(event.site.uuid, [event])
  }

  return events.filter((event) => {
    const isCandidate =
      event.source === EventSource.enum.power_production &&
      event.event_type === EventType.enum.stop &&
      !event.is_validated &&
      event.silence_notes.length === 0
    if (!isCandidate) return false

    const explainingEvents = explainingBySite.get(event.site.uuid) ?? []

    return !explainingEvents.some((other) => other.start < event.end && other.end > event.start)
  })
}

// Same shape as the zefire endpoint planned to replace it on large perimeters.
export interface DailyStopsToCheck {
  site_uuid: string
  // local day start
  date: Date
  n_to_check: number
}

type StopToCheck = Pick<EventSchema, "site" | "start" | "end">

// Stops to check (see `getStopsToReview`) per site and local day (non-zero only), each counted on
// every day it touches.
export function countStopsToCheckPerSiteDay(stops: StopToCheck[]): DailyStopsToCheck[] {
  const counts = new Map<string, DailyStopsToCheck>()

  for (const stop of stops) {
    const days = dt.eachDayOfInterval({
      start: stop.start,
      end: dt.max([stop.start, dt.subMilliseconds(stop.end, 1)]),
    })

    for (const day of days) {
      const date = dt.startOfDay(day)
      const key = `${stop.site.uuid}|${date.getTime()}`
      const row = counts.get(key) ?? { site_uuid: stop.site.uuid, date, n_to_check: 0 }

      row.n_to_check += 1
      counts.set(key, row)
    }
  }

  return [...counts.values()]
}

// Stops to check of each site overlapping a range (end excluded), each counted once whatever the
// days it spans: unlike the daily counts, they add up to the number of stops to check.
export function countStopsToCheckPerSite(
  stops: StopToCheck[],
  range: DateRange,
): Map<string, number> {
  const bySite = new Map<string, number>()

  for (const stop of stops) {
    if (stop.start >= range.end || stop.end <= range.start) continue

    bySite.set(stop.site.uuid, (bySite.get(stop.site.uuid) ?? 0) + 1)
  }

  return bySite
}

// Validation filter, applied client-side: events of read-only sources (connectors) can never be
// validated, so they are kept whatever the choice. The backend `is_validated` param would drop them.
export function matchesValidatedChoice(
  event: EventSchema,
  choice: IsValidatedChoices | null | undefined,
): boolean {
  if (!isEventEditable(event) || R.isNullish(choice) || choice === "both") {
    return true
  }

  return event.is_validated === (choice === "validated")
}

// Sources whose validated events feed the operation KPIs; the others are informative only.
// Mirrors `EventSource.for_availabilities` in zefire/logic/supervision/event_schema.py.
export const KPI_EVENT_SOURCES: EventSchema["source"][] = ["manual", "power_production", "ai"]

// __________ Event visual encoding __________
// Shared by the timeline and calendar views: the fill color is the cause, the fill pattern the type
// (solid stop, hatched downgraded, outlined informative), the border the validation and the left
// stripe the source. The soft tone lightens it for the calendar views, where the bars fill most of
// the grid instead of standing out on the pale timeline rows.

export type EventTone = "strong" | "soft"

export function getEventFillStyle(
  event: EventSchema,
  tone: EventTone = "strong",
): { background: string; color: string } {
  const color = mapEventCauseColor[event.cause ?? EventCause.enum.unknown]
  const isSoft = tone === "soft"
  const textColor = getUIColorHex(color, isSoft ? 800 : 900)

  return {
    [EventType.enum.stop]: {
      background: getUIColorHex(color, isSoft ? 100 : 400),
      color: textColor,
    },
    [EventType.enum.downgraded]: {
      background: isSoft
        ? getHatchedBackground(getUIColorHex(color, 200), getUIColorHex(color, 50))
        : getHatchedBackground(getUIColorHex(color, 300), getUIColorHex(color, 100)),
      color: textColor,
    },
    [EventType.enum.informative]: {
      background: "rgba(255, 255, 255, 0.8)",
      color: getUIColorHex(color, 700),
    },
  }[event.event_type]
}

// Solid green once validated, dashed amber while awaiting it. Read-only connector mirrors can never
// be validated: they keep a neutral thin gray border. The soft tone thins the validation border.
export function getEventBorderStyle(
  event: EventSchema,
  tone: EventTone = "strong",
): {
  borderColor: string
  borderWidth: string
  borderStyle: "solid" | "dashed"
} {
  const isSoft = tone === "soft"

  if (!isEventEditable(event)) {
    return {
      borderColor: getUIColorHex("gray", isSoft ? 300 : 400),
      borderWidth: "1px",
      borderStyle: "solid",
    }
  }

  return {
    borderColor: getUIColorHex(event.is_validated ? "lime" : "amber", isSoft ? 500 : 600),
    borderWidth: isSoft ? "1px" : "2px",
    borderStyle: event.is_validated ? "solid" : "dashed",
  }
}

// Inset shadow rather than a border, so that it does not conflict with the validation border.
export function getEventSourceStripeStyle(event: EventSchema): { boxShadow: string } {
  return { boxShadow: `inset 3px 0 0 ${getUIColorHex(mapEventSourceColor[event.source], 500)}` }
}

// Priority order mirrors the backend `pick_best_of_mode`: prefer the precise
// rolling-PR estimate, fall back to the budget-based one.
export const ENERGY_ESTIMATION_MODE_PRIORITY: EnergyEstimationMode[] = [
  "rolling_pr_30days",
  "budget",
]

// Resolve the losses to display: the requested mode when available, otherwise the
// highest-priority available mode (best-of), matching the backend `default_losses`.
export function getEventLosses(
  losses: EventSchema["losses"] | null | undefined,
  mode?: EnergyEstimationMode | null,
): EventLosses | undefined {
  if (R.isNullish(losses)) return undefined
  if (R.isNonNullish(mode) && R.isNonNullish(losses[mode])) return losses[mode]
  const best = ENERGY_ESTIMATION_MODE_PRIORITY.find((m) => R.isNonNullish(losses[m]))
  return best ? losses[best] : undefined
}

// __________ Clauses utils __________

// Mirrors the pd.cut banding of zefire's NegativePrice clause (clause_logic_neg_price.py):
// thresholds sorted ascending, each price type applies to spot prices in
// [previous_threshold, own_threshold). Returned from the band closest to 0 down to the
// open-ended lowest band (lower === null).
export function getNegPriceBands(
  priceType: Record<string, number>,
): { type: string; lower: number | null; upper: number }[] {
  const sorted = R.entries(priceType).sort(([, a], [, b]) => a - b)
  return sorted
    .map(([type, threshold], idx) => ({
      type,
      lower: idx === 0 ? null : sorted[idx - 1][1],
      upper: threshold,
    }))
    .reverse()
}

// __________ Business Objects utils __________

export function deduceBusinessObj(obj: InvoiceComputeTarget): "site" | "contract" | "invoice" {
  if ("network_voltage" in obj && "commissioning" in obj) {
    return "site" as const
  } else if ("is_birthday" in obj && "is_accounted" in obj) {
    return "invoice" as const
  } else if ("purchase_order" in obj && "price_mechanism" in obj) {
    return "contract" as const
  } else {
    throw new Error("Could not categorize object")
  }
}

// __________ Note business utils __________

export function getNoteLevelAgg(notes: NoteSchema[]): NoteTagLevel | undefined {
  const levelOrdeRMap: Record<NoteTagLevel, number> = {
    error: 0,
    warning: 1,
    action: 2,
    info: 3,
  }

  const levels = R.pipe(
    notes,
    R.map(R.prop("level")),
    R.filter(R.isNonNullish),
    R.unique(),
    R.sortBy((level) => levelOrdeRMap[level as NoteTagLevel]),
  ) as NoteTagLevel[]

  if (levels.length === 0) {
    return undefined
  } else {
    return levels[0]
  }
}

export function getNotePath(target_model: NoteQueryModelName, target_uuid: string): string {
  let path: string = `/${target_model}/${target_uuid}`

  const company_models = [
    NoteQueryModelName.enum.assetmanager,
    NoteQueryModelName.enum.assetowner,
    NoteQueryModelName.enum.holding,
  ] as string[]

  if (company_models.includes(target_model)) {
    path = `/companies/${target_model}/${target_uuid}`
  } else if (target_model === NoteQueryModelName.enum.site) {
    path = `/portfolio/${target_uuid}`
  } else if (target_model === NoteQueryModelName.enum.contract) {
    path = `/contracts/${target_uuid}`
  } else if (target_model === NoteQueryModelName.enum.invoice) {
    path = `/invoices/${target_uuid}`
  }

  return path
}

// Commissioning reserves / Notes
export const reserveConstraints = {
  requiredTagNames: ["Réserve"],
  requiredCategories: ["temporalité", "criticité"],
  categoriesUnique: [
    "temporalité",
    "criticité",
    "non-conformité",
    "responsabilité",
    "réception travaux",
  ],
}

// __________ Contact Infos utils __________

export function getContactFullName(
  contact: ContactInfoSchema,
  formatter: (value: string) => string,
): string {
  if (R.isNonNullish(contact.firstname) || R.isNonNullish(contact.lastname)) {
    return [contact.firstname, contact.lastname].join(" ").toUpperCase()
  }
  return (
    contact.email ??
    contact.position ??
    contact.team_name ??
    formatter("company.contact.anonymous")
  ).toUpperCase()
}
export function getContactTwoLettersRepr(
  contact: ContactInfoSchema,
  formatter: (value: string) => string,
): string {
  return getContactFullName(contact, formatter)
    .split(" ")
    .map((word) => word.charAt(0))
    .join("")
    .substring(0, 2)
}

// __________ GoldenSource finance utils __________

/**
 * Split a goldensource finance stack key into its status and price mechanism.
 * Stack keys are "<status>_<price_mechanism>" at site/contract level but
 * "<status>" alone at company aggregation levels. Statuses may themselves
 * contain underscores (e.g. budget_recalled), so the longest known status
 * prefix wins.
 */
export function splitGSFinanceStackKey(
  stackKey: string,
  statuses: string[],
): { status: string; mechanism: string } | undefined {
  const status = R.pipe(
    statuses,
    R.filter((s) => stackKey === s || stackKey.startsWith(`${s}_`)),
    R.firstBy([(s) => s.length, "desc"]),
  )
  if (R.isNullish(status)) return undefined
  return { status, mechanism: stackKey.slice(status.length + 1) }
}

// __________ Commissioning __________

export function isSiteFailingPerformanceTest(site: CommissioningSite): boolean {
  return (
    !site.has_passed_performance_test && // still not passed the test
    R.isNumber(site.number_perf_test_failed) &&
    site.number_perf_test_failed >= 3 && // has fail more than 3 times
    !site.missing_data // we have data
  )
}
